import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaPlay, FaPause, FaTimes, FaGithub, FaLinkedin, FaEnvelope, FaRedo, FaAward,
  FaFutbol, FaCode, FaLeaf, FaChartLine, FaArrowUp,
} from "react-icons/fa";
import { SKILLS, ACHIEVEMENTS, EXPERIENCE } from "../pages/About";
import { PROJECTS } from "../pages/Projects";
import { useReveal, Dialog, createIntroSound } from "./Netflix";
import me from "../assets/IMG_1267.jpg";
import "../css/Upgrade.css";

const jump = id => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

/* ---------- Scroll effects: top progress bar + hero parallax (CSS reads --hero-p) ---------- */
export function ScrollFx() {
  const bar = useRef(null);
  useEffect(() => {
    let raf = 0;
    const root = document.documentElement;
    const calc = () => {
      raf = 0;
      const y = window.scrollY;
      const max = root.scrollHeight - window.innerHeight;
      if (bar.current) bar.current.style.transform = `scaleX(${max > 0 ? Math.min(1, y / max) : 0})`;
      root.style.setProperty("--hero-p", Math.min(1, y / (window.innerHeight * 0.9)).toFixed(3));
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(calc); };
    calc();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      root.style.removeProperty("--hero-p");
    };
  }, []);
  return <div className="nx-progress" ref={bar} aria-hidden="true" />;
}

/* ---------- Hover tilt (parallax) for cards: sets --rx / --ry, CSS does the 3D transform ---------- */
const tiltMove = e => {
  const el = e.currentTarget;
  const r = el.getBoundingClientRect();
  const x = (e.clientX - r.left) / r.width - 0.5;
  const y = (e.clientY - r.top) / r.height - 0.5;
  el.style.setProperty("--ry", `${(x * 14).toFixed(2)}deg`);
  el.style.setProperty("--rx", `${(-y * 14).toFixed(2)}deg`);
  el.style.setProperty("--gx", `${((x + 0.5) * 100).toFixed(0)}%`);
  el.style.setProperty("--gy", `${((y + 0.5) * 100).toFixed(0)}%`);
};
const tiltEnd = e => {
  const el = e.currentTarget;
  el.style.setProperty("--rx", "0deg");
  el.style.setProperty("--ry", "0deg");
};

/* ---------- Opening scene: name blurs in, your portrait rises with a red glow, press Enter ---------- */
export function Opening({ onDone }) {
  const [phase, setPhase] = useState("in"); /* in -> ready -> out */
  const snd = useRef(null);
  const done = useRef(onDone);
  done.current = onDone;

  useEffect(() => {
    const t = setTimeout(() => setPhase(p => (p === "in" ? "ready" : p)), 2600);
    return () => clearTimeout(t);
  }, []);
  useEffect(() => () => { if (snd.current) snd.current.stop(false); }, []);

  const enter = () => {
    if (phase === "out") return;
    setPhase("out");
    snd.current = createIntroSound(); /* started by a click, so the browser allows the sound */
    setTimeout(() => done.current(), 900);
  };

  return (
    <div className={`nx-open ${phase}`} onClick={enter}>
      <div className="nx-open-glow" aria-hidden="true" />
      <h1 className="nx-open-name">Aayam Bhattarai</h1>
      <p className="nx-open-sub">The series</p>
      <div className="nx-open-photo" aria-hidden="true"><img src={me} alt="" /></div>
      <p className="nx-open-role">Frontend developer</p>
      <button type="button" className="nx-open-btn" onClick={e => { e.stopPropagation(); enter(); }} autoFocus>
        <FaPlay /> Enter
      </button>
    </div>
  );
}

/* ---------- Play Intro: a short auto-playing trailer (no video file needed) ---------- */
const REEL = [
  { k: "Aayam Bhattarai", s: "Frontend developer", big: true },
  { k: `${PROJECTS.length} projects`, s: "Web, mobile and data science" },
  { k: `${ACHIEVEMENTS.length} certifications`, s: "Python, analytics and scouting" },
  { k: "IPITEX 2026", s: "Bangkok international expo" },
  { k: "Retainovo", s: "Founder, building for brands" },
  { k: "To be continued…", s: "Scroll for the full series", last: true },
];
const STEP = 2800;

export function IntroReel({ onClose }) {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const last = i === REEL.length - 1;

  useEffect(() => {
    if (paused || last) return undefined;
    const t = setTimeout(() => setI(n => n + 1), STEP);
    return () => clearTimeout(t);
  }, [i, paused, last]);

  const s = REEL[i];
  const explore = () => { onClose(); setTimeout(() => jump("story"), 80); };

  return (
    <Dialog className="nx-reel" label="Intro reel" onClose={onClose}>
      <div className="nx-reel-bg" style={{ "--photo": `url(${me})` }} aria-hidden="true" />
      <button className="nx-x" onClick={onClose} aria-label="Close intro"><FaTimes /></button>
      <div className="nx-reel-bars" aria-hidden="true">
        {REEL.map((_, n) => (
          <span key={n} className={n < i ? "done" : n === i ? "on" : ""}>
            <i style={{ animationDuration: `${STEP}ms`, animationPlayState: paused ? "paused" : "running" }} />
          </span>
        ))}
      </div>
      <div className="nx-reel-slide" key={i} aria-live="polite">
        <p className="nx-reel-eye">{i + 1} / {REEL.length}</p>
        <h3 className={s.big ? "big" : ""}>{s.k}</h3>
        <p className="nx-reel-sub">{s.s}</p>
        {last && (
          <div className="nx-reel-btns">
            <button className="nx-btn nx-btn-w" onClick={explore}><FaPlay /> Start watching</button>
            <button className="nx-btn nx-btn-g" onClick={() => { setI(0); setPaused(false); }}><FaRedo /> Watch again</button>
          </div>
        )}
      </div>
      {!last && (
        <button className="nx-reel-pp" onClick={() => setPaused(p => !p)} aria-label={paused ? "Play intro" : "Pause intro"}>
          {paused ? <FaPlay /> : <FaPause />}
        </button>
      )}
    </Dialog>
  );
}

/* ---------- "Continue exploring": quick-jump cards under the hero ---------- */
const QUICK = [
  { id: "story",    t: "The Full Story",  s: "Who I am",              from: "#e50914", to: "#4a070c" },
  { id: "moments",  t: "Top Moments",     s: "Highlights",            from: "#f5a623", to: "#6b3504" },
  { id: "skills",   t: "Skill Universe",  s: `${SKILLS.length} skills`, from: "#0a7cff", to: "#0a2552" },
  { id: "projects", t: "Originals",       s: `${PROJECTS.length} projects`, from: "#9b5de5", to: "#2d1650" },
  { id: "journey",  t: "My Journey",      s: "Season by season",      from: "#1db954", to: "#083d1e" },
];

export function QuickRow() {
  return (
    <section className="nx-quick" aria-label="Continue exploring">
      <h2>Continue exploring</h2>
      <div className="nx-qtrack">
        {QUICK.map((q, i) => (
          <button key={q.id} className="nx-q" style={{ "--g1": q.from, "--g2": q.to }} onClick={() => jump(q.id)}>
            <span className="nx-qn" aria-hidden="true">{i + 1}</span>
            <b>{q.t}</b>
            <small>{q.s}</small>
          </button>
        ))}
      </div>
    </section>
  );
}

/* ---------- Top Moments: gold award cards ---------- */
const ach = t => ACHIEVEMENTS.find(a => a.title === t);
const MOMENTS = [
  { tag: "International expo", title: "IPITEX 2026", line: "On the floor at Bangkok's international invention and technology exposition.", go: "memories", cta: "See the photos" },
  { tag: "Founder", title: "Retainovo", line: "Building and running Klaviyo flows and campaigns for brands.", go: "experience", cta: "See experience" },
  { tag: "Data analytics", title: "Data Analyst in Python", line: "36-hour DataCamp track, 2026.", cert: ach("Data Analyst in Python") },
  { tag: "Membership", title: "IoA Student Member", line: "Institute of Analytics, 2024.", cert: ach("IoA: Institute of Analytics") },
  { tag: "Football scouting", title: "Scouting & Recruitment", line: "Two modules on positional analysis and recruitment, 2024.", cert: ach("Scouting & Recruitment: Module 1") },
  { tag: "Shipped", title: `${PROJECTS.length} projects`, line: "Web, mobile and data science, all completed.", go: "projects", cta: "See originals" },
];

export function TopMoments({ onOpen }) {
  const [ref, rv] = useReveal();
  return (
    <section id="moments" className={`nx-sec ${rv}`} ref={ref} aria-label="Top moments">
      <p className="nx-eye">Awards season</p>
      <h2 className="nx-sh">Top moments</h2>
      <div className="nx-mgrid">
        {MOMENTS.map((m, i) => {
          const act = m.cert
            ? () => onOpen({ type: "image", src: m.cert.img, alt: m.cert.title })
            : m.go ? () => jump(m.go) : null;
          const cta = m.cert ? "View certificate" : m.cta;
          const Tag = act ? "button" : "div";
          return (
            <Tag key={m.title} className="nx-mc" style={{ "--i": i }} onClick={act || undefined}
              onMouseMove={tiltMove} onMouseLeave={tiltEnd}>
              <span className="nx-mc-tag">{m.tag}</span>
              <FaAward className="nx-mc-ic" aria-hidden="true" />
              <h3>{m.title}</h3>
              <p>{m.line}</p>
              {act && <span className="nx-mc-go">{cta} ›</span>}
            </Tag>
          );
        })}
      </div>
    </section>
  );
}

/* ---------- Skill Universe: categories left, skills right ---------- */
const CATS = [
  { name: "Frontend",       note: "Interfaces & the web",       items: ["HTML / CSS", "JavaScript", "React", "UI / UX"] },
  { name: "Backend",        note: "Servers, APIs & realtime",   items: ["Node.js", "Express.js", "Firebase", "Socket.io"] },
  { name: "Mobile",         note: "Apps for phones",            items: ["Android", "Java", "Flutter"] },
  { name: "Data & ML",      note: "Analysis & models",          items: ["Python", "TensorFlow", "Jupyter Notebook"] },
  { name: "Databases",      note: "Storing & querying data",    items: ["MongoDB"] },
  { name: "Tools & Growth", note: "Shipping & marketing",       items: ["Git", "Trello", "Cloudflare", "Klaviyo"] },
  { name: "Interests",      note: "Off the keyboard",
    fun: [
      { name: "Football", icon: FaFutbol,    line: "Tactics, scouting and match analysis" },
      { name: "Coding",   icon: FaCode,      line: "Stubborn scripts and clean interfaces" },
      { name: "Nature",   icon: FaLeaf,      line: "Quiet rides and fresh air" },
      { name: "Data",     icon: FaChartLine, line: "Finding the pattern in the numbers" },
    ] },
];

export function SkillUniverse() {
  const [ref, rv] = useReveal();
  const [ci, setCi] = useState(0);
  const cat = CATS[ci];
  const list = cat.items ? cat.items.map(n => SKILLS.find(s => s.name === n)).filter(Boolean) : [];
  return (
    <section id="skills" className={`nx-sec ${rv}`} ref={ref} aria-label="Skill universe">
      <p className="nx-eye">Genres</p>
      <h2 className="nx-sh">My skill universe</h2>
      <div className="nx-uni">
        <div className="nx-ucats" role="tablist" aria-label="Skill categories">
          {CATS.map((c, i) => (
            <button key={c.name} role="tab" aria-selected={ci === i} className={`nx-ucat${ci === i ? " on" : ""}`}
              onMouseEnter={() => setCi(i)} onFocus={() => setCi(i)} onClick={() => setCi(i)}>
              {c.name}<small>{c.note}</small>
            </button>
          ))}
        </div>
        <div className="nx-upanel" key={cat.name} role="tabpanel">
          {cat.fun
            ? cat.fun.map((f, i) => (
                <div className="nx-ut nx-ut-fun" key={f.name} style={{ "--i": i, "--c": "#f5a623" }}>
                  <div className="nx-ut-top"><f.icon /></div>
                  <b>{f.name}</b><small>{f.line}</small>
                </div>
              ))
            : list.map((s, i) => (
                <div className="nx-ut" key={s.name} style={{ "--i": i, "--c": s.color, "--pct": `${s.pct}%` }}>
                  <div className="nx-ut-top"><s.icon /><em>{s.pct}%</em></div>
                  <b>{s.name}</b><small>{s.tag}</small>
                  <span className="nx-ut-bar"><i /></span>
                </div>
              ))}
        </div>
      </div>
      <p className="nx-rnote">Percentages are my own self-assessment, not a formal test.</p>
    </section>
  );
}

/* ---------- The Full Story ---------- */
const FACTS = [
  ["Now",       "Founder, Retainovo"],
  ["Studying",  "BSc (Hons) Computing"],
  ["Stack",     "JavaScript · React · Node.js · Python"],
  ["Based in",  "Bhaktapur, Nepal"],
  ["Languages", "English · Nepali"],
];

export function FullStory() {
  const [ref, rv] = useReveal();
  return (
    <section id="story" className={`nx-sec ${rv}`} ref={ref} aria-label="The full story">
      <p className="nx-eye">Episode 1</p>
      <h2 className="nx-sh">The full story</h2>
      <div className="nx-story">
        <div className="nx-st-photo" onMouseMove={tiltMove} onMouseLeave={tiltEnd}>
          <img src={me} alt="Aayam Bhattarai" loading="lazy" />
          <span>Aayam Bhattarai</span>
        </div>
        <div className="nx-st-body">
          <blockquote className="nx-st-q">
            “Whether I'm breaking down a defense on the pitch, debugging a stubborn script, or riding
            through nature's quiet paths, I'm all about passion, precision, and discovery.”
          </blockquote>
          <div className="nx-fx">
            {FACTS.map(([k, v]) => <div key={k}><small>{k}</small><b>{v}</b></div>)}
          </div>
          <div className="nx-tags">
            {["Football", "Coding", "Nature", "Data"].map(t => <span key={t}>{t}</span>)}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- My Journey: seasons by year, built from your real data ---------- */
const PAL = [["#e50914", "#4a070c"], ["#0a7cff", "#0a2552"], ["#1db954", "#083d1e"], ["#f5a623", "#6b3504"], ["#9b5de5", "#2d1650"], ["#00bbf9", "#052f40"]];

function buildSeasons() {
  const items = [
    ...ACHIEVEMENTS.map(a => ({ year: a.year, title: a.title, sub: a.cat, text: a.desc })),
    ...EXPERIENCE.map(e => ({ year: /^\d{4}/.test(e.year) ? e.year.slice(0, 4) : "Now", title: e.title, sub: e.org, text: e.desc })),
    { year: "2026", title: "IPITEX 2026, Bangkok", sub: "Expo", text: "Photos and videos from the international invention and technology exposition." },
  ];
  const by = {};
  items.forEach(it => { (by[it.year] = by[it.year] || []).push(it); });
  const years = Object.keys(by).sort((a, b) => (a === "Now") - (b === "Now") || a.localeCompare(b));
  return years.map((y, i) => ({ label: y === "Now" ? "Now" : y, name: `Season ${i + 1}`, eps: by[y] }));
}
const SEASONS = buildSeasons();

export function Journey() {
  const [ref, rv] = useReveal();
  const [si, setSi] = useState(0);
  const s = SEASONS[si];
  return (
    <section id="journey" className={`nx-sec ${rv}`} ref={ref} aria-label="My journey">
      <p className="nx-eye">Series timeline</p>
      <h2 className="nx-sh">My journey</h2>
      <div className="nx-seasons" role="tablist" aria-label="Seasons">
        {SEASONS.map((x, i) => (
          <button key={x.name} role="tab" aria-selected={si === i} className={`nx-season${si === i ? " on" : ""}`} onClick={() => setSi(i)}>
            {x.name} · {x.label}
          </button>
        ))}
      </div>
      <div className="nx-etrack" key={s.name}>
        {s.eps.map((e, i) => {
          const [g1, g2] = PAL[(i + si) % PAL.length];
          return (
            <article className="nx-ep" key={e.title} style={{ "--i": i, "--g1": g1, "--g2": g2 }}>
              <div className="nx-ep-img"><span aria-hidden="true">{i + 1}</span></div>
              <div className="nx-ep-b">
                <small>Episode {i + 1} · {s.label}</small>
                <h3>{e.title}</h3>
                <em>{e.sub}</em>
                <p>{e.text}</p>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

/* ---------- To Be Continued… finale ---------- */
export function Finale() {
  const [ref, rv] = useReveal();
  const replay = () => {
    try { sessionStorage.removeItem("nx-intro"); } catch (e) { /* ignore */ }
    window.location.reload();
  };
  return (
    <section className={`nx-finale${rv.includes("in") ? " go" : ""}`} ref={ref} aria-labelledby="nx-fin-h">
      <p className="nx-fin-eye">The next episode</p>
      <h2 id="nx-fin-h">To be continued…</h2>
      <p className="nx-fin-sub">Aayam Bhattarai · Frontend developer</p>
      <div className="nx-fin-btns">
        <Link className="nx-btn nx-btn-r" to="/contact"><FaPlay /> Let's talk</Link>
        <a className="nx-btn nx-btn-g" href="https://np.linkedin.com/in/aayam-bhattarai-1ba53b343" target="_blank" rel="noopener noreferrer"><FaLinkedin /> LinkedIn</a>
        <a className="nx-btn nx-btn-g" href="https://github.com/aayam17" target="_blank" rel="noopener noreferrer"><FaGithub /> GitHub</a>
        <a className="nx-btn nx-btn-g" href="mailto:aayambht@gmail.com"><FaEnvelope /> Email</a>
      </div>
      <div className="nx-fin-links">
        <button type="button" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}><FaArrowUp /> Watch again</button>
        <button type="button" onClick={replay}><FaRedo /> Replay opening</button>
      </div>
    </section>
  );
}
